import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Divider,
  Tab,
  Tabs,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Timer as TimerIcon,
  Http as HttpIcon,
  Code as CodeIcon,
  Payment as PaymentIcon,
  AccountCircle as CustomerIcon,
  CreditCard as CardIcon,
  Subscriptions as SubscriptionIcon,
  Notifications as NotificationIcon,
  Settings as SystemIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { debugService, DebugTestResult } from '../services/debugService';
import PaymentFlowDialog from '../components/PaymentFlowDialog';
import { API_BASE_URL } from '../constants';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`debug-tabpanel-${index}`}
      aria-labelledby={`debug-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Debug: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<DebugTestResult[]>([]);
  const [selectedResult, setSelectedResult] = useState<DebugTestResult | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [paymentFlowDialogOpen, setPaymentFlowDialogOpen] = useState(false);
  const [oneTimePaymentDialogOpen, setOneTimePaymentDialogOpen] = useState(false);
  const [oneTimePaymentFormData, setOneTimePaymentFormData] = useState({
    productId: 'test_product_onetime',
    productName: 'テスト用ワンタイム決済商品',
    amount: '1000',
    currency: 'JPY',
    description: 'テスト用ワンタイム決済',
    returnUrl: `https://minmitest.javis.vn/payments/complete/debug_test`,
  });

  const basicTestEndpoints = [
    {
      name: 'ヘルスチェック',
      description: 'サーバーの稼働状況を確認',
      action: () => debugService.testHealthCheck(),
      color: 'info' as const,
      icon: <SystemIcon />,
    },
    {
      name: '認証テスト',
      description: '現在のユーザー情報を取得',
      action: () => debugService.testAuth(),
      color: 'primary' as const,
      icon: <CustomerIcon />,
    },
    {
      name: 'ダッシュボード統計',
      description: 'ダッシュボード用統計データを取得',
      action: () => debugService.testDashboardStats(),
      color: 'secondary' as const,
      icon: <SystemIcon />,
    },
  ];

  const paymentTestEndpoints = [
    {
      name: '決済一覧',
      description: '決済データ一覧を取得',
      action: () => debugService.testPayments(),
      color: 'success' as const,
      icon: <PaymentIcon />,
    },
    {
      name: 'テスト決済作成',
      description: 'POST APIでワンタイム決済を作成し、eSCOTT画面を新タブで開く',
      action: () => {
        // No need to generate customer ID for simplified API
        setOneTimePaymentDialogOpen(true);
        return Promise.resolve({
          endpoint: 'Dialog opened',
          method: 'DIALOG',
          status: 200,
          duration: 0,
          success: true,
          data: { message: 'ワンタイム決済作成ダイアログを開きました' },
          timestamp: new Date().toISOString()
        });
      },
      color: 'primary' as const,
      icon: <PaymentIcon />,
    },
  ];

  const customerTestEndpoints = [
    {
      name: '顧客一覧',
      description: '顧客データ一覧を取得',
      action: () => debugService.testCustomers(),
      color: 'info' as const,
      icon: <CustomerIcon />,
    },
  ];

  const subscriptionTestEndpoints = [
    {
      name: 'サブスクリプション作成',
      description: 'サブスクリプション作成（4MemAdd: 会員登録+カード登録）',
      action: () => debugService.testSubscriptionCardRegistration(),
      color: 'primary' as const,
      icon: <AddIcon />,
    },
    {
      name: 'サブスク一覧取得',
      description: '作成済みサブスクリプション一覧を確認',
      action: () => debugService.testSubscriptions(),
      color: 'info' as const,
      icon: <SubscriptionIcon />,
    },
    {
      name: '継続課金実行',
      description: '会員ベース継続課金決済テスト',
      action: () => debugService.testSubscriptionRecurringPayment(),
      color: 'warning' as const,
      icon: <PaymentIcon />,
    },
    {
      name: 'サブスク管理操作',
      description: '一時停止・再開・キャンセル操作',
      action: () => debugService.testSubscriptionManagement(),
      color: 'error' as const,
      icon: <SubscriptionIcon />,
    },
  ];

  const systemTestEndpoints = [
    {
      name: 'システム状態',
      description: 'システム詳細状態を取得',
      action: () => debugService.testSystemStatus(),
      color: 'info' as const,
      icon: <SystemIcon />,
    },
    {
      name: 'システムメトリクス',
      description: 'システムパフォーマンス情報を取得',
      action: () => debugService.testSystemMetrics(),
      color: 'success' as const,
      icon: <SystemIcon />,
    },
    {
      name: '通知送信テスト',
      description: 'テスト通知を送信',
      action: () => debugService.testSendNotification(),
      color: 'warning' as const,
      icon: <NotificationIcon />,
    },
    {
      name: 'Webhook管理',
      description: 'Webhookエンドポイント一覧を取得',
      action: () => debugService.testWebhookEndpoints(),
      color: 'error' as const,
      icon: <SystemIcon />,
    },
  ];

  const handleSingleTest = async (testAction: () => Promise<DebugTestResult>) => {
    console.log('🧪 Starting single test...');
    setLoading(true);
    try {
      const result = await testAction();
      console.log('✅ Test completed successfully:', result);
      setResults(prev => {
        const newResults = [result, ...prev];
        console.log('📊 Updated results:', newResults);
        return newResults;
      });
      setSelectedResult(result);
      
      // Auto-switch to results tab to show the result
      setTabValue(5);
    } catch (error) {
      console.error('❌ Test failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAllTests = async () => {
    setLoading(true);
    try {
      const allResults = await debugService.testAllEndpoints();
      setResults(prev => [...allResults, ...prev]);
    } catch (error) {
      console.error('All tests failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setResults([]);
    setSelectedResult(null);
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 400 && status < 500) return 'warning';
    if (status >= 500) return 'error';
    return 'default';
  };

  const formatDuration = (duration: number) => {
    return `${duration}ms`;
  };

  const formatJson = (data: any) => {
    return JSON.stringify(data, null, 2);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOneTimePaymentSubmit = async () => {
    console.log('=== DEBUG: One-time payment submit clicked ===');
    console.log('oneTimePaymentFormData:', oneTimePaymentFormData);
    
    setLoading(true);
    try {
      // Create custom one-time payment test with user-defined parameters
      const testResult = await debugService.testCreatePaymentWithParams({
        productId: oneTimePaymentFormData.productId,
        productName: oneTimePaymentFormData.productName,
        amount: parseInt(oneTimePaymentFormData.amount),
        currency: oneTimePaymentFormData.currency,
        description: oneTimePaymentFormData.description,
        returnUrl: oneTimePaymentFormData.returnUrl,
      });
      
      setResults(prev => [testResult, ...prev]);
      setSelectedResult(testResult);
      setOneTimePaymentDialogOpen(false);
      setTabValue(5); // Switch to results tab
      
      console.log('One-time payment test completed:', testResult);
    } catch (error) {
      console.error('One-time payment test failed:', error);
    } finally {
      setLoading(false);
    }
  };


  const renderTestCards = (endpoints: any[]) => (
    <Box display="flex" flexWrap="wrap" gap={2}>
      {endpoints.map((test, index) => (
        <Box key={index} sx={{ minWidth: 300, flex: '1 1 calc(33.333% - 16px)' }}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                {test.icon}
                <Typography variant="h6">
                  {test.name}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {test.description}
              </Typography>
              <Button
                variant="contained"
                color={test.color}
                startIcon={<PlayIcon />}
                onClick={() => handleSingleTest(test.action)}
                disabled={loading}
                fullWidth
                size="small"
              >
                テスト実行
              </Button>
            </CardContent>
          </Card>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            APIデバッグツール
          </Typography>
          <Typography variant="body1" color="text.secondary">
            バックエンドAPIのテストと決済フローの動作確認を行います。
          </Typography>
        </Box>
        
        <Fab
          color="primary"
          variant="extended"
          onClick={() => setPaymentFlowDialogOpen(true)}
          sx={{ minWidth: 200 }}
        >
          <PaymentIcon sx={{ mr: 1 }} />
          決済フロー テスト
        </Fab>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="debug tabs">
          <Tab label="基本機能" />
          <Tab label="都度決済" />
          <Tab label="顧客" />
          <Tab label="サブスクリプション" />
          <Tab label="システム・通知" />
          <Tab label={`テスト結果 (${results.length})`} />
        </Tabs>
      </Box>

      {/* Debug Status Bar */}
      {process.env.NODE_ENV === 'development' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          🔧 デバッグ情報: 結果数={results.length} | ローディング={loading ? 'true' : 'false'} | 最新結果={selectedResult?.endpoint || 'なし'}
        </Alert>
      )}

      <TabPanel value={tabValue} index={0}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<PlayIcon />}
              onClick={handleAllTests}
              disabled={loading}
              size="large"
            >
              全テスト実行
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearResults}
              disabled={loading || results.length === 0}
            >
              結果クリア
            </Button>
            {loading && <CircularProgress size={24} />}
          </Box>
          
          <Typography variant="h6" gutterBottom>
            基本機能テスト
          </Typography>
          {renderTestCards(basicTestEndpoints)}
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            都度決済機能テスト
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            決済フローの詳細テストは右上の「決済フロー テスト」ボタンから実行できます。
          </Alert>
          {renderTestCards(paymentTestEndpoints)}
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            顧客管理機能テスト
          </Typography>
          {renderTestCards(customerTestEndpoints)}
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            サブスクリプション機能テスト
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>サブスクリプション決済フロー:</strong><br />
            Phase 1: カード登録 → Phase 2: 4MemAdd（会員登録）→ 1Gathering（初回決済）→ Phase 3: 継続課金（会員ベース）
          </Alert>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>技術的改善:</strong> このシステムでは4MemAdd → 1Gatheringの順序で実装し、セキュリティと信頼性を向上させています。
          </Alert>
          {renderTestCards(subscriptionTestEndpoints)}
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            システム・通知機能テスト
          </Typography>
          {renderTestCards(systemTestEndpoints)}
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={5}>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            テスト結果 ({results.length}件)
          </Typography>
          
          {results.length === 0 ? (
            <Alert severity="info">
              まだテストが実行されていません。他のタブからテストを実行してください。
            </Alert>
          ) : (
            <>
            <TableContainer sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>時刻</TableCell>
                    <TableCell>エンドポイント</TableCell>
                    <TableCell>メソッド</TableCell>
                    <TableCell>ステータス</TableCell>
                    <TableCell>レスポンス時間</TableCell>
                    <TableCell>結果</TableCell>
                    <TableCell>詳細</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((result, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        {new Date(result.timestamp).toLocaleTimeString('ja-JP')}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <HttpIcon sx={{ mr: 1, fontSize: 16 }} />
                          <Typography variant="body2" fontFamily="monospace">
                            {result.endpoint}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={result.method} 
                          size="small" 
                          color={result.method === 'GET' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={result.status}
                          size="small"
                          color={getStatusColor(result.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TimerIcon sx={{ mr: 1, fontSize: 16 }} />
                          {formatDuration(result.duration)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {result.success ? (
                          <SuccessIcon color="success" />
                        ) : (
                          <ErrorIcon color="error" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<CodeIcon />}
                          onClick={() => setSelectedResult(result)}
                        >
                          詳細
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {selectedResult && (
              <Accordion expanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    詳細レスポンス: {selectedResult.endpoint} 
                    <Chip 
                      label={selectedResult.status} 
                      size="small" 
                      color={getStatusColor(selectedResult.status)}
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" flexWrap="wrap" gap={2}>
                    <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 300 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        リクエスト情報
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="body2" fontFamily="monospace">
                          Method: {selectedResult.method}<br />
                          Endpoint: {selectedResult.endpoint}<br />
                          Duration: {formatDuration(selectedResult.duration)}<br />
                          Timestamp: {selectedResult.timestamp}
                        </Typography>
                      </Paper>
                      
                      {selectedResult.error && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            エラー情報
                          </Typography>
                          <Alert severity="error">
                            {selectedResult.error}
                          </Alert>
                        </Box>
                      )}
                    </Box>
                    
                    <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 300 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        レスポンスデータ
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 400, overflow: 'auto' }}>
                        <Typography variant="body2" fontFamily="monospace" component="pre">
                          {selectedResult.data ? formatJson(selectedResult.data) : 'No data'}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={() => setSelectedResult(null)}
                    >
                      閉じる
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
            </>
          )}
        </Paper>
      </TabPanel>

      {/* Connection Info */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          接続情報
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>API Base URL:</strong> {process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>認証トークン:</strong> {localStorage.getItem('authToken') ? '設定済み' : '未設定'}
        </Typography>
      </Paper>
      
      {/* Payment Flow Dialog */}
      <PaymentFlowDialog 
        open={paymentFlowDialogOpen}
        onClose={() => setPaymentFlowDialogOpen(false)}
      />

      {/* One-time Payment Creation Dialog */}
      <Dialog open={oneTimePaymentDialogOpen} onClose={() => setOneTimePaymentDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          💳 デバッグ用ワンタイム決済作成
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            eSCOTT連携テスト用の詳細設定
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* 基本設定 */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
              📋 基本設定
            </Typography>
            
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="商品名 *"
                  value={oneTimePaymentFormData.productName}
                  onChange={(e) => setOneTimePaymentFormData({ ...oneTimePaymentFormData, productName: e.target.value })}
                  margin="normal"
                  required
                  helperText="eSCOTT: MerchantFree2 | 商品・サービス名（わかりやすい名前）"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="決済金額 *"
                  type="number"
                  value={oneTimePaymentFormData.amount}
                  onChange={(e) => setOneTimePaymentFormData({ ...oneTimePaymentFormData, amount: e.target.value })}
                  margin="normal"
                  required
                  helperText="eSCOTT: Amount | 決済金額（円）"
                  InputProps={{
                    endAdornment: <Typography variant="body2" color="text.secondary">円</Typography>
                  }}
                />
              </Grid>
            </Grid>

            {/* 通貨設定 */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
              💱 通貨設定
            </Typography>
            
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>通貨</InputLabel>
                  <Select
                    value={oneTimePaymentFormData.currency}
                    onChange={(e) => setOneTimePaymentFormData({ ...oneTimePaymentFormData, currency: e.target.value })}
                    label="通貨"
                  >
                    <MenuItem value="JPY">🇯🇵 JPY (日本円)</MenuItem>
                    <MenuItem value="USD">🇺🇸 USD (米ドル)</MenuItem>
                    <MenuItem value="EUR">🇪🇺 EUR (ユーロ)</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    eSCOTT: Amount単位として使用 | 決済通貨
                  </Typography>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="決済説明"
              value={oneTimePaymentFormData.description}
              onChange={(e) => setOneTimePaymentFormData({ ...oneTimePaymentFormData, description: e.target.value })}
              margin="normal"
              helperText="eSCOTT: MerchantFree3 | eSCOTTの決済画面に表示される説明文（オプション）"
            />

            {/* オプション設定 */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
              ⚙️ オプション設定
            </Typography>
            
            <TextField
              fullWidth
              label="リダイレクトURL"
              value={oneTimePaymentFormData.returnUrl}
              onChange={(e) => setOneTimePaymentFormData({ ...oneTimePaymentFormData, returnUrl: e.target.value })}
              margin="normal"
              helperText="eSCOTT: RedirectUrl | 決済完了後のリダイレクト先URL（オプション）"
              placeholder="https://your-site.com/payment/complete"
            />

            {/* eSCOTT自動設定項目 */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
              🔧 eSCOTT自動設定項目
            </Typography>
            
            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mt: 1, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>OperateId:</strong> 1Auth（ワンタイム決済）
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>PayType:</strong> 01（都度決済）
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>PostUrl:</strong> https://minmitest.javis.vn/api/webhooks/escott
              </Typography>
              <Typography variant="body2">
                <strong>TransactionId:</strong> 自動生成（12桁）
              </Typography>
            </Box>

            {/* プレビュー */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
              👀 設定プレビュー
            </Typography>
            
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>決済金額:</strong> {oneTimePaymentFormData.amount}円 ({oneTimePaymentFormData.currency})
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>商品名:</strong> {oneTimePaymentFormData.productName}
              </Typography>
              {oneTimePaymentFormData.description && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>説明:</strong> {oneTimePaymentFormData.description}
                </Typography>
              )}
              {oneTimePaymentFormData.returnUrl && (
                <Typography variant="body2">
                  <strong>リダイレクト先:</strong> {oneTimePaymentFormData.returnUrl}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setOneTimePaymentDialogOpen(false)}
            size="large"
          >
            キャンセル
          </Button>
          <Button 
            onClick={handleOneTimePaymentSubmit} 
            variant="contained"
            size="large"
            disabled={!oneTimePaymentFormData.productName || !oneTimePaymentFormData.amount || loading}
            startIcon={<span>🚀</span>}
            sx={{ minWidth: 200 }}
          >
            デバッグテスト開始
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Debug;
